/**
 * 🎬 Video Service
 * DOM動画要素の操作に特化したサービス
 *
 * 責任:
 * - 動画要素の作成・管理
 * - 動画の読み込み・再生・停止
 * - 表示切り替え
 */

/**
 * Video element identifier type
 */
type VideoElementId = 'A' | 'B';

/**
 * Video attributes for configuration
 */
interface VideoAttributes {
    loop?: boolean;
    muted?: boolean;
    autoplay?: boolean;
    [key: string]: unknown;
}

/**
 * Video state information
 */
interface VideoState {
    src: string;
    currentTime: number;
    duration: number;
    paused: boolean;
    readyState: number;
    networkState: number;
    error: MediaError | null;
}

/**
 * Video Service configuration options
 */
interface VideoServiceConfig {
    containerId?: string;
    loadTimeout?: number;
}

declare global {
    interface Window {
        VideoService?: typeof VideoService;
    }
}

class VideoService {
    private videoElementA: HTMLVideoElement | null;
    private videoElementB: HTMLVideoElement | null;
    private activeElement: VideoElementId | null;
    private container: HTMLElement | null;
    private readonly loadTimeout: number;

    constructor(config?: VideoServiceConfig) {
        this.videoElementA = null;
        this.videoElementB = null;
        this.activeElement = null;
        this.container = null;
        this.loadTimeout = config?.loadTimeout ?? 10000;
    }
    
    /**
     * 初期化
     */
    public initialize(containerId: string = 'video-container'): boolean {
        // コンテナを取得（IDまたはクラスセレクタに対応）
        if (containerId.startsWith('.') || containerId.startsWith('#')) {
            // セレクタとして処理
            this.container = document.querySelector<HTMLElement>(containerId);
        } else {
            // IDとして処理
            this.container = document.getElementById(containerId);
        }

        // フォールバック: character-only-panelを使用
        if (!this.container) {
            logger.warn(`Container ${containerId} not found, trying fallback...`);
            this.container = document.querySelector<HTMLElement>('.character-only-panel');

            if (!this.container) {
                // 最終フォールバック: 既存の動画要素の親を使用
                const existingVideo = document.getElementById('background-video') as HTMLVideoElement | null;
                if (existingVideo?.parentNode && existingVideo.parentNode instanceof HTMLElement) {
                    this.container = existingVideo.parentNode;
                    logger.warn('Using existing video parent as container');
                } else {
                    logger.error(`No suitable container found for video system`);
                    return false;
                }
            } else {
            }
        } else {
        }

        // 動画要素を作成
        this.createVideoElements();

        // 初期状態を設定
        this.setActiveElement('A');

        return true;
    }
    
    /**
     * 動画要素の作成
     */
    private createVideoElements(): void {
        if (!this.container) {
            throw new Error('Container not initialized');
        }

        // 既存の background-video 要素を再利用（優先）
        const existingVideo = document.getElementById('background-video') as HTMLVideoElement | null;
        if (existingVideo) {
            this.videoElementA = existingVideo;
            this.videoElementA.id = 'background-video-a';

            // 既存要素のスタイルを統一
            this.applyVideoStyles(this.videoElementA);

            // B要素は新規作成
            this.videoElementB = this.createVideoElement('background-video-b');
            this.container.appendChild(this.videoElementB);
        } else {
            // 新規作成
            this.videoElementA = this.createVideoElement('background-video-a');
            this.videoElementB = this.createVideoElement('background-video-b');

            // コンテナに追加
            this.container.appendChild(this.videoElementA);
            this.container.appendChild(this.videoElementB);
        }

        // 初期状態設定
        this.videoElementA.style.display = 'block';
        this.videoElementB.style.display = 'none';

    }
    
    /**
     * 動画要素にスタイルを適用
     */
    private applyVideoStyles(video: HTMLVideoElement): void {
        video.className = 'background-video';
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('webkit-playsinline', '');

        // 統一スタイル設定
        video.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: 0;
            border-radius: 20px;
        `;
    }

    /**
     * 単一の動画要素を作成
     */
    private createVideoElement(id: string): HTMLVideoElement {
        const video = document.createElement('video');
        video.id = id;

        // 統一スタイルを適用
        this.applyVideoStyles(video);

        // エラーハンドリング
        video.addEventListener('error', (e: ErrorEvent) => {
            logger.error(`Video error on ${id}:`, e);
        });

        return video;
    }
    
    /**
     * 動画を読み込み
     */
    public async loadVideo(element: HTMLVideoElement, src: string): Promise<void> {
        if (!element || !src) {
            throw new Error('Invalid parameters for loadVideo');
        }

        return new Promise<void>((resolve, reject) => {
            let isResolved = false;

            const cleanup = (): void => {
                element.removeEventListener('loadeddata', onLoadedData);
                element.removeEventListener('error', onError);
            };

            const onLoadedData = (): void => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    resolve();
                }
            };

            const onError = (): void => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    reject(new Error(`Failed to load video: ${src}`));
                }
            };

            // イベントリスナー設定
            element.addEventListener('loadeddata', onLoadedData);
            element.addEventListener('error', onError);

            // 動画ソースを設定
            element.src = src;
            element.load();

            // タイムアウト設定
            setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    cleanup();
                    reject(new Error(`Video load timeout: ${src}`));
                }
            }, this.loadTimeout);
        });
    }
    
    /**
     * 動画を再生
     */
    public async playVideo(element: HTMLVideoElement): Promise<void> {
        if (!element) {
            throw new Error('Invalid video element');
        }

        try {
            await element.play();
        } catch (error: unknown) {
            // 自動再生ポリシーエラーの場合はミュート再生を試行
            if (error instanceof DOMException && error.name === 'NotAllowedError') {
                element.muted = true;
                await element.play();
            } else {
                throw error;
            }
        }
    }

    /**
     * 動画を一時停止
     */
    public async pauseVideo(element: HTMLVideoElement): Promise<void> {
        if (!element) {
            throw new Error('Invalid video element');
        }

        element.pause();

        // srcをクリアしてメモリを解放
        if (element !== this.getActiveVideoElement()) {
            element.src = '';
            element.load();
        }
    }
    
    /**
     * アクティブな要素を設定
     */
    public setActiveElement(elementId: VideoElementId): void {
        if (!this.videoElementA || !this.videoElementB || !this.container) {
            throw new Error('Video elements not initialized');
        }


        if (elementId === 'A') {
            this.videoElementA.style.display = 'block';
            this.videoElementA.style.zIndex = '10';
            this.videoElementB.style.display = 'none';
            this.videoElementB.style.zIndex = '1';
            this.activeElement = 'A';
        } else {
            this.videoElementA.style.display = 'none';
            this.videoElementA.style.zIndex = '1';
            this.videoElementB.style.display = 'block';
            this.videoElementB.style.zIndex = '10';
            this.activeElement = 'B';
        }

        // DOM構造の確認
    }
    
    /**
     * 動画要素を取得
     */
    public getVideoElementA(): HTMLVideoElement | null {
        return this.videoElementA;
    }

    public getVideoElementB(): HTMLVideoElement | null {
        return this.videoElementB;
    }

    public getActiveVideoElement(): HTMLVideoElement | null {
        return this.activeElement === 'A' ? this.videoElementA : this.videoElementB;
    }

    public getInactiveVideoElement(): HTMLVideoElement | null {
        return this.activeElement === 'A' ? this.videoElementB : this.videoElementA;
    }
    
    /**
     * 動画属性を設定
     */
    public setVideoAttributes(element: HTMLVideoElement, attributes: VideoAttributes): void {
        Object.keys(attributes).forEach((key) => {
            const value = attributes[key];
            if (key === 'loop' || key === 'muted' || key === 'autoplay') {
                // Type-safe property assignment for boolean video properties
                (element as unknown as Record<string, unknown>)[key] = value;
            } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                element.setAttribute(key, String(value));
            }
        });
    }

    /**
     * 動画の状態を取得
     */
    public getVideoState(element: HTMLVideoElement | null): VideoState | null {
        if (!element) return null;

        return {
            src: element.src,
            currentTime: element.currentTime,
            duration: element.duration,
            paused: element.paused,
            readyState: element.readyState,
            networkState: element.networkState,
            error: element.error,
        };
    }
    
    /**
     * クリーンアップ
     */
    public cleanup(): void {
        if (this.videoElementA) {
            void this.pauseVideo(this.videoElementA);
            this.videoElementA.remove();
        }

        if (this.videoElementB) {
            void this.pauseVideo(this.videoElementB);
            this.videoElementB.remove();
        }

        this.videoElementA = null;
        this.videoElementB = null;
        this.activeElement = null;
    }
}

// Export types
export type { VideoElementId, VideoAttributes, VideoState, VideoServiceConfig };
import { logger } from '@/utils/logger.js';

// ES6 Export
export { VideoService };

if (typeof module !== 'undefined' && module.exports) {
    module.exports = VideoService;
}
